import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ToolChip } from '@backrs_ui/grow-ui/src/components/Nuggets/ToolChip';
import {
  ALL_STICKERS,
  StickerCardId,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';

import { Box, Link, SxProps, Theme, Typography, TypographyProps } from '@mui/material';
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown';

type GuideMarkdownVariant = Extract<
  TypographyProps['variant'],
  'text-md-regular' | 'text-sm-regular'
>;

type GuideMarkdownProps = Readonly<{
  /** Markdown source — bold, links and lists only. */
  children: string;
  variant?: GuideMarkdownVariant;
}>;

/** Href prefix marking a link to a Backrs tool, e.g. '(tool:college_shortlist)'. */
const TOOL_HREF_PREFIX = 'tool:';

const BOLD_VARIANTS = {
  'text-md-regular': 'text-md-semibold',
  'text-sm-regular': 'text-sm-semibold',
} as const;

const toolIdFromHref = (href?: string): StickerCardId | undefined => {
  if (!href?.startsWith(TOOL_HREF_PREFIX)) return undefined;

  const toolId = href.slice(TOOL_HREF_PREFIX.length);

  return toolId in ALL_STICKERS ? (toolId as StickerCardId) : undefined;
};

/** Keeps tool hrefs, which the default transform drops as an unknown protocol. */
const urlTransform = (url: string) =>
  url.startsWith(TOOL_HREF_PREFIX) ? url : defaultUrlTransform(url);

/** Guide markdown as plain text: link labels only, no emphasis markers. */
export const guideMarkdownToText = (markdown: string): string =>
  markdown
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1');

/** Guide copy written as markdown, with `tool:` links rendered as inline tool chips. */
export const GuideMarkdown: React.FC<GuideMarkdownProps> = ({
  children,
  variant = 'text-md-regular',
}) => {
  const navigate = useNavigate();

  const components: Components = useMemo(
    () => ({
      p: ({ children: content }) => (
        <Typography variant={variant} sx={paragraphSx}>
          {content}
        </Typography>
      ),
      strong: ({ children: content }) => (
        <Typography component="strong" variant={BOLD_VARIANTS[variant]}>
          {content}
        </Typography>
      ),
      ul: ({ children: content }) => (
        <Box component="ul" sx={listSx}>
          {content}
        </Box>
      ),
      li: ({ children: content }) => (
        <Typography component="li" variant={variant} sx={listItemSx}>
          {content}
        </Typography>
      ),
      a: ({ href, children: content }) => {
        const toolId = toolIdFromHref(href);

        if (toolId) {
          return <ToolChip toolId={toolId} label={content} />;
        }

        // An unknown tool id would otherwise render an anchor to a scheme the browser can't open.
        if (href?.startsWith(TOOL_HREF_PREFIX)) {
          return <>{content}</>;
        }

        const isInternal = href?.startsWith('/');

        return (
          <Link
            href={href}
            sx={linkSx}
            {...(isInternal
              ? {
                  onClick: (event: React.MouseEvent) => {
                    event.preventDefault();
                    if (href) navigate(href);
                  },
                }
              : { target: '_blank', rel: 'noopener noreferrer' })}
          >
            {content}
          </Link>
        );
      },
    }),
    [navigate, variant],
  );

  return (
    <ReactMarkdown components={components} urlTransform={urlTransform}>
      {children}
    </ReactMarkdown>
  );
};

const paragraphSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  m: backrs.spacing['spacing-none'],
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  m: backrs.spacing['spacing-none'],
  pl: backrs.spacing['spacing-2xl'],
});

const listItemSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const linkSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Blue/500'],
  textDecorationColor: backrs.colors['Blue/500'],
  fontWeight: 'bold',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.7,
  },
});
